import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import ShowToRole from 'components/Common/Portal/ShowToRole'
import Spinning from 'components/Common/Spinner/Spinning'
import { CadExViewer } from 'components/ModelViewers/CadEx/CadExViewer'
import { ViewerErrorBoundary } from 'components/ModelViewers/CadExViewer/ViwerErrorBoundary'
import { FormDetails } from 'components/ProjectPage/BillOfMaterials/RowDetails/FormDetails'
import { BomItemController } from 'controllers/Project/BomItemController'
import { useController } from 'customHooks/useController'
import { ItemTypeDetail } from 'model/ItemTypeDetail'
import {
  AssemblyHeaderRow,
  AssemblyRow,
  BoMItemType,
  PartTypeRow,
} from 'model/Project/BoMItemRow'
import React, { Suspense, useEffect } from 'react'
import { useAppSelector } from 'store/configureStore'
import { ShowException } from 'store/Application/appActions'
import { RootAppState } from 'store/configureStore'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import './RowDetailsDialog.scss'

// const CadExViewer = React.lazy(
//   () => import('components/ModelViewers/CadExViewer/CadExViewer')
// )

export type RowDetailsDialogProps = {
  rowId: string
  open: boolean
  onClose: () => void
  bomItemType: BoMItemType
}

export const RowDetailsDialog = (props: RowDetailsDialogProps) => {
  const row = useAppSelector(
    (state: RootAppState) =>
      (state.project.flatView[props.rowId] ||
        state.project.treeView[props.rowId]) as
      | PartTypeRow
      | AssemblyHeaderRow
      | AssemblyRow
  )
  const isSeller = useAppSelector(
    (state: RootAppState) => !state.project.activeProject.isBuyingPartyView
  )

  const { partyId, projectId } = useOrganizationAndProjectContext()
  const [rowDetails, setRowDetails] = React.useState<ItemTypeDetail>(undefined)
  const { controller, loading } = useController(
    () => new BomItemController(partyId, projectId)
  )

  useEffect(() => {
    ; (async () => {
      try {
        const rowDetails = await controller.GetItemDetails(props.rowId)
        setRowDetails(rowDetails)
      } catch (err) {
        ShowException('row details', err)
      }
    })()
  }, [controller, props.rowId])

  let objFileName = ''

  if (row.type === 'Row' || row.type === 'Assembly') {
    objFileName = row.threeDModelPath
  } else if (row.type === 'AssemblyHeader') {
    objFileName = row.assembly.threeDModelPath
  }

  if (!objFileName) {
    return null
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="xl"
      keepMounted={false}
    >
      <DialogTitle>
        {row.type === 'Row' || row.type === 'Assembly'
          ? row.name
          : row.assembly.name}
      </DialogTitle>
      <DialogContent className="row-details--container">
        <Suspense fallback={<Spinning />}>
          <div className={`row-models ${!isSeller ? 'full-height' : ''}`}>
            <div className="model-viewer">
              <ViewerErrorBoundary onClose={props.onClose}>
                <CadExViewer
                  partyId={partyId}
                  projectId={projectId}
                  itemId={row['geometryId'] || props.rowId}
                />
              </ViewerErrorBoundary>
            </div>
            {row.type === 'Row' && row.twoDModelPath && (
              <div className="model-viewer">
                <ViewerErrorBoundary onClose={props.onClose}>
                  <CadExViewer
                    partyId={partyId}
                    projectId={projectId}
                    itemId={row['geometryId'] || props.rowId}
                    is2DModel={true}
                  />
                </ViewerErrorBoundary>
              </div>
            )}
          </div>
          <ShowToRole seller>
            <LoadingContainer
              loading={loading['get-item-details']}
              empty={
                (!rowDetails || rowDetails?.categories === null) && <div />
              }
              noMessage
            >
              <div className="row-details">
                <FormDetails formDetails={rowDetails?.categories} />
              </div>
            </LoadingContainer>
          </ShowToRole>
        </Suspense>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} variant="outlined">
          close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
